class UpsellProducts {
    constructor() {
        this.container = document.querySelector(
            ".upsell-products-container"
        );
        if (!this.container) return;

        this.listContainer = this.container.querySelector(
            ".upsell-products-list"  
        );
        this.items = this.container.querySelectorAll(
            ".upsell-product-item"
        );

        // Get settings from data attributes
        this.totalProducts =
            parseInt(this.listContainer?.dataset.totalProducts) ||
            this.items.length;

        this.init();
    }

    init() {
        this.setupVariantSelectors();
        this.setupAddToCartForms();

        // Wait for product recommendations to load
        this.waitForProducts();
    }

    waitForProducts() {
        // Check if products are already loaded
        if (this.items.length > 0) {
            return;
        }

        // Also listen for product-recommendations loaded class
        const productRecommendations =
            this.container.querySelector(
                "product-recommendations"
            );
        if (productRecommendations) {
            // Check if already loaded
            if (
                productRecommendations.classList.contains(
                    "product-recommendations--loaded"
                )
            ) {
                this.recheckItems();
                return;
            }

            // Watch for loaded class to be added
            const observer = new MutationObserver(
                (mutations) => {
                    mutations.forEach((mutation) => {
                        if (
                            mutation.type ===
                                "attributes" &&
                            mutation.attributeName ===
                                "class" &&
                            productRecommendations.classList.contains(
                                "product-recommendations--loaded"
                            )
                        ) {
                            observer.disconnect();
                            setTimeout(
                                () => this.recheckItems(),
                                50
                            );
                        }
                    });
                }
            );

            observer.observe(productRecommendations, {
                attributes: true,
                attributeFilter: ["class"],
            });
        }

        // Wait for product recommendations to load
        const checkInterval = setInterval(() => {
            this.recheckItems(checkInterval);
        }, 200);

        // Stop checking after 5 seconds
        setTimeout(() => {
            clearInterval(checkInterval);
            if (this.items.length === 0) {
                console.warn(
                    "No upsell products found after 5 seconds"
                );
            }
        }, 5000);
    }

    recheckItems(intervalToCancel = null) {
        // Re-query for items in case they were added after initial load
        this.items = this.container.querySelectorAll(
            ".upsell-product-item"
        );

        if (this.items.length > 0) {
            if (intervalToCancel) {
                clearInterval(intervalToCancel);
            }

            // Re-setup event listeners for newly loaded products
            this.setupVariantSelectors();
            this.setupAddToCartForms();
        }
    }


    setupVariantSelectors() {
        const selectors = this.container.querySelectorAll(
            ".upsell-variant-select"
        );

        selectors.forEach((selector) => {
            // Initialize with current selected value
            this.handleVariantChange(selector);
            
            selector.addEventListener("change", (e) => {
                this.handleVariantChange(e.target);
            });
        });
    }

    setupAddToCartForms() {
        const forms = this.container.querySelectorAll(
            ".upsell-add-to-cart-form"
        );

        forms.forEach((form) => {
            form.addEventListener("submit", (e) => {
                e.preventDefault();
                this.handleAddToCart(form);
            });
        });
    }

    handleVariantChange(selector) {
        const productItem = selector.closest(
            ".upsell-product-item"
        );
        const priceContainer = productItem.querySelector(
            ".upsell-product-price"
        );
        const addToCartBtn = productItem.querySelector(
            ".upsell-add-to-cart-btn"
        );

        const selectedOption =
            selector.options[selector.selectedIndex];
        const variantId = selectedOption.value;
        const variantPrice = selectedOption.dataset.price;
        const isAvailable = !selectedOption.disabled;


        // Update the hidden variant ID input in the form within this product item
        const form = productItem.querySelector('.upsell-add-to-cart-form');
        if (form) {
            const variantInput = form.querySelector('.upsell-variant-id');
            if (variantInput) {
                variantInput.value = variantId;
            } else {
                console.error('No variant input found in form');
            }
        } else {
            console.error('No form found in product item');
        }

        // Also sync other variant selectors in the same product item
        const allSelectorsInProduct = productItem.querySelectorAll('.upsell-variant-select');
        allSelectorsInProduct.forEach(otherSelector => {
            if (otherSelector !== selector && otherSelector.value !== variantId) {
                otherSelector.value = variantId;
            }
        });

        // Update button state
        if (addToCartBtn) {
            if (isAvailable) {
                addToCartBtn.disabled = false;
                addToCartBtn.textContent =
                    window.theme.translations.upsell
                        .add || "Add";
            } else {
                addToCartBtn.disabled = true;
                addToCartBtn.textContent =
                    window.theme.translations.sold_out ||
                    "Sold Out";
            }
        }

        // Update price if variant price is different
        if (variantPrice && priceContainer) {
            this.updatePrice(priceContainer, variantPrice);
        }
    }

    updatePrice(priceContainer, newPrice) {
        // Format price according to shop currency settings
        const formattedPrice = this.formatMoney(newPrice);
        const priceElement =
            priceContainer.querySelector(
                ".price-current"
            ) ||
            priceContainer.querySelector(".price") ||
            priceContainer;

        if (priceElement) {
            priceElement.textContent = formattedPrice;
        }
    }

    formatMoney(cents) {
        // Basic money formatting - you might want to use Shopify's money formatting
        const amount = cents / 100;
        return new Intl.NumberFormat("es-ES", {
            style: "currency",
            currency: "EUR",
            minimumFractionDigits: 2,
        }).format(amount);
    }

    async handleAddToCart(form) {
        const btn = form.querySelector(
            ".upsell-add-to-cart-btn"
        );
        const originalText = btn.textContent;


        // Add loading state
        btn.classList.add("loading");
        btn.disabled = true;

        try {
            const formData = new FormData(form);
            const variantId = formData.get('id');
            const quantity = formData.get('quantity') || 1;
            

            // Use the newer items API format
            const requestBody = {
                items: [{
                    id: parseInt(variantId),
                    quantity: parseInt(quantity)
                }]
            };

            const response = await fetch("/cart/add.js", {
                method: "POST",
                body: JSON.stringify(requestBody),
                headers: {
                    "Content-Type": "application/json",
                    "X-Requested-With": "XMLHttpRequest",
                    Accept: "application/json",
                },
            });
            

            if (response.ok) {
                const responseData = await response.json();

                // Success state
                btn.classList.remove("loading");
                btn.classList.add("success");
                btn.textContent =
                    window.theme.translations.upsell
                        .added_to_cart || "Added!";

                // Use the same sections approach as main product
                this.fetchAndRenderCartSections();

                // Dispatch the same events as main product
                dispatchCustomEvent("cart:item-added", {
                    product: responseData.hasOwnProperty("items") 
                        ? responseData.items[0] 
                        : responseData,
                });

                // Reset button after 2 seconds
                setTimeout(() => {
                    btn.classList.remove("success");
                    btn.textContent = originalText;
                    btn.disabled = false;
                }, 2000);
            } else {
                let errorData;
                try {
                    errorData = await response.json();
                } catch (e) {
                    errorData = { message: `HTTP ${response.status}: ${response.statusText}` };
                }
                console.error(
                    "Cart add error response:",
                    errorData
                );
                console.error("Response headers:", [...response.headers.entries()]);
                throw new Error(
                    errorData.message ||
                        `Failed to add to cart: ${response.status}`
                );
            }
        } catch (error) {
            console.error("Error adding to cart:", error);

            // Error state
            btn.classList.remove("loading");
            btn.textContent =
                window.theme.translations.upsell
                    .error_adding_to_cart || "Error";

            // Reset button after 2 seconds
            setTimeout(() => {
                btn.textContent = originalText;
                btn.disabled = false;
            }, 2000);

            // Show error notification if available
            if (
                window.theme &&
                window.theme.showNotification
            ) {
                window.theme.showNotification(
                    error.message,
                    "error"
                );
            }
        }
    }


    getSectionsToRender() {
        return [
            {
                id: "Cart",
                section: "main-cart",
                selector: ".thb-cart-form",
            },
            {
                id: "Cart-Drawer",
                section: "cart-drawer",
                selector: ".cart-drawer",
            },
            {
                id: "cart-drawer-toggle",
                section: "cart-bubble",
                selector: ".thb-item-count",
            },
        ];
    }

    fetchAndRenderCartSections() {
        // Fetch updated cart sections using the same method as main product
        const sections = this.getSectionsToRender().map(section => section.section).join(',');
        
        fetch(`${window.location.pathname}?sections=${sections}`)
            .then(response => response.text())
            .then(text => {
                const parsedState = JSON.parse(text);
                this.renderContents({ sections: parsedState });
            })
            .catch(error => {
                console.error('Error fetching cart sections:', error);
                // Fallback to simple update
                this.triggerCartUpdate();
                this.openCartDrawer();
            });
    }

    renderContents(parsedState) {
        // Use the exact same logic as app.js renderContents method
        this.getSectionsToRender().forEach((section) => {
            if (!document.getElementById(section.id)) {
                return;
            }
            const elementToReplace =
                document
                    .getElementById(section.id)
                    .querySelector(section.selector) ||
                document.getElementById(section.id);
            elementToReplace.innerHTML = this.getSectionInnerHTML(
                parsedState.sections[section.section],
                section.selector
            );

            if (section.id === "Cart-Drawer") {
                document
                    .getElementById("Cart-Drawer")
                    ?.notesToggle?.();
                document
                    .getElementById("Cart-Drawer")
                    ?.removeProductEvent?.();
            }

            if (section.id === "Cart" && typeof Cart !== "undefined") {
                new Cart().renderContents(parsedState);
            }
        });

        // Open cart drawer with the same logic as app.js
        if (document.getElementById("Cart-Drawer")) {
            document.getElementById("Cart-Drawer").classList.add("active");
            document.body.classList.add("open-cart");
            document.body.classList.add("open-cc");
            
            // Hide smile-ui-lite-container when opening cart drawer via upsell
            const smileContainer = document.getElementById("smile-ui-lite-container");
            if (smileContainer) {
                smileContainer.style.display = "none";
            }
            
            if (document.getElementById("Cart-Drawer").querySelector(".product-recommendations--full")) {
                document.getElementById("Cart-Drawer")
                    .querySelector(".product-recommendations--full")
                    .classList.add("active");
            }
            
            dispatchCustomEvent("cart-drawer:open");
        }
    }

    getSectionInnerHTML(html, selector = ".shopify-section") {
        return new DOMParser()
            .parseFromString(html, "text/html")
            .querySelector(selector).innerHTML;
    }

    openCartDrawer() {
        // Hide smile-ui-lite-container when opening cart drawer from upsell
        const smileContainer = document.getElementById("smile-ui-lite-container");
        if (smileContainer) {
            smileContainer.style.display = "none";
        }

        // Use the same method as main product to dispatch cart drawer open event
        if (typeof dispatchCustomEvent === 'function') {
            dispatchCustomEvent("cart-drawer:open");
        } else {
            // Fallback if dispatchCustomEvent is not available
            document.dispatchEvent(new CustomEvent("cart-drawer:open"));
        }
    }

    triggerCartUpdate() {
        // Update cart count and drawer
        fetch("/cart.js")
            .then((response) => response.json())
            .then((cart) => {
                // Update cart count elements
                const cartCountElements =
                    document.querySelectorAll(
                        "[data-cart-count], .cart-count, .header-cart-count"
                    );
                cartCountElements.forEach((element) => {
                    element.textContent = cart.item_count;
                    element.setAttribute(
                        "data-cart-count",
                        cart.item_count
                    );
                });

                // Update cart bubble/icon if it exists
                const cartBubble =
                    document.querySelector(".cart-bubble");
                if (cartBubble) {
                    cartBubble.textContent =
                        cart.item_count;
                    if (cart.item_count > 0) {
                        cartBubble.style.display = "block";
                    }
                }

                // Update cart drawer if it exists
                if (window.CartDrawer) {
                    window.CartDrawer.updateCartDrawer();
                } else if (
                    window.cart &&
                    window.cart.refresh
                ) {
                    window.cart.refresh();
                }

                // Try to update any cart sections
                const cartSections =
                    document.querySelectorAll(
                        '[data-section-type="cart"]'
                    );
                cartSections.forEach((section) => {
                    if (section.refresh) {
                        section.refresh();
                    }
                });

                // Show success notification
                if (
                    window.theme &&
                    window.theme.showNotification
                ) {
                    window.theme.showNotification(
                        "Product added to cart!",
                        "success"
                    );
                }
            })
            .catch((error) =>
                console.error(
                    "Error updating cart count:",
                    error
                )
            );
    }

}

// Initialize when DOM is loaded
document.addEventListener("DOMContentLoaded", () => {
    const upsellContainer = document.querySelector(
        ".upsell-products-container"
    );
    if (upsellContainer) {
        new UpsellProducts();
    }
});

// Re-initialize when new content is loaded (for AJAX)
document.addEventListener("shopify:section:load", () => {
    new UpsellProducts();
});

// Debug: log when product recommendations load
document.addEventListener("DOMContentLoaded", () => {
    const observer = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
            if (mutation.type === "childList") {
                mutation.addedNodes.forEach((node) => {
                    if (
                        node.nodeType === 1 &&
                        node.classList &&
                        node.classList.contains(
                            "upsell-product-item"
                        )
                    ) {
                    }
                });
            }
        });
    });

    const upsellContainer = document.querySelector(
        ".upsell-products-container"
    );
    if (upsellContainer) {
        observer.observe(upsellContainer, {
            childList: true,
            subtree: true,
        });
    }
});
